import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";

import PublicLayout from "@/pages/public/PublicLayout";
import Home from "@/pages/public/Home";
import Jobs from "@/pages/public/Jobs";
import JobDetail from "@/pages/public/JobDetail";
import About from "@/pages/public/About";
import Services from "@/pages/public/Services";
import AreaOfExpertise from "@/pages/public/AreaOfExpertise";
import Careers from "@/pages/public/Careers";
import Countries from "@/pages/public/Countries";
import ForEmployers from "@/pages/public/ForEmployers";
import ForAgencies from "@/pages/public/ForAgencies";
import Contact from "@/pages/public/Contact";
import Legal from "@/pages/public/Legal";

import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";

import CandidatePortal from "@/pages/app/CandidatePortal";
import AgencyPortal from "@/pages/app/AgencyPortal";
import AdminApp from "@/pages/admin/AdminApp";

function Loading() {
  return (
    <div className="min-h-screen grid place-items-center bg-background">
      <div className="h-10 w-10 rounded-full border-4 border-slate-200 border-t-[#0088FF] animate-spin" />
    </div>
  );
}

function RoleHome() {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "candidate") return <Navigate to="/app/candidate" replace />;
  if (user.role === "agency") return <Navigate to="/app/agency" replace />;
  return <Navigate to="/admin" replace />;
}

function Protected({ roles, children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role) && user.role !== "super_admin")
    return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/jobs" element={<Jobs />} />
            <Route path="/jobs/:id" element={<JobDetail />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services />} />
            <Route path="/area-of-expertise" element={<AreaOfExpertise />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/countries" element={<Countries />} />
            <Route path="/for-employers" element={<ForEmployers />} />
            <Route path="/for-agencies" element={<ForAgencies />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<Legal />} />
            <Route path="/terms" element={<Legal />} />
            <Route path="/account-deletion" element={<Legal />} />
          </Route>

          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/dashboard" element={<RoleHome />} />

          <Route
            path="/app/candidate/*"
            element={
              <Protected roles={["candidate"]}>
                <CandidatePortal />
              </Protected>
            }
          />
          <Route
            path="/app/agency/*"
            element={
              <Protected roles={["agency"]}>
                <AgencyPortal />
              </Protected>
            }
          />
          <Route
            path="/admin/*"
            element={
              <Protected roles={["admin", "recruitment_staff", "hr", "finance"]}>
                <AdminApp />
              </Protected>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
